/**
 * B4 DESK/JOB governance contract.
 *
 * This module is deliberately independent of desk-runtime and the control
 * routes. It validates metadata and provides an append-only, bounded audit
 * sink without granting mutation authority.
 */

export type GovernanceAgent = 'DESK' | 'JOB'
export type GovernanceCapability = 'observe' | 'plan' | 'validate' | 'mutate'
export type MutationAuthority = 'none' | 'approve_plan' | 'confirmed_control'
export type RiskTier = 'conservative' | 'balanced' | 'moderate' | 'aggressive'

export type EconomicTraceability = {
  riskTier: RiskTier
  exposureDelta: number
  entitlementTier: string
  costImpact: number
}

export type GovernanceMetadata = {
  agent: GovernanceAgent
  capability: GovernanceCapability
  mutationAuthority: MutationAuthority
  decisionId: string
  parentDecisionId?: string
  stateHash: string
  lineage: string[]
  economic: EconomicTraceability
}

export type GovernanceAuditKind =
  | 'control_action'
  | 'arbitration'
  | 'verifier_failure'
  | 'economic_guardrail'
  | 'job_state'
  | 'policy_event'
  | 'workflow_event'
  | 'simulation_event'
  | 'autonomy_window_event'

export type GovernanceAuditEntry = {
  id: string
  kind: GovernanceAuditKind
  agent: GovernanceAgent
  decisionId: string
  stateHash: string
  parentStateHash?: string
  metadata: GovernanceMetadata
  details: Record<string, string | number | boolean>
  occurredAt: string
}

export type GovernanceCursorPage = {
  entries: readonly GovernanceAuditEntry[]
  nextCursor: string | null
  hasMore: boolean
}

export type GovernanceTimelineEntry = {
  id: string
  occurredAt: string
  kind: GovernanceAuditKind
  agent: GovernanceAgent
  phase: string
  decisionId: string
  stateHash: string
  economic: EconomicTraceability
  details: Record<string, string | number | boolean>
  replayToken: string
}

export type GovernanceValidation = { valid: boolean; errors: string[] }

const MAX_ENTRIES = 400
const MAX_LINEAGE = 8
const MAX_DETAILS = 12
const MAX_STRING = 240
const RISK_TIERS = new Set<RiskTier>(['conservative', 'balanced', 'moderate', 'aggressive'])

export function stableGovernanceId(value: unknown): string {
  const raw = stableSerialize(value)
  let hash = 2166136261
  for (let i = 0; i < raw.length; i += 1) {
    hash ^= raw.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(16).padStart(8, '0')
}

export function validateEconomicTraceability(economic: unknown): GovernanceValidation {
  const errors: string[] = []
  if (!economic || typeof economic !== 'object') return { valid: false, errors: ['economic'] }
  const value = economic as Partial<EconomicTraceability>
  if (!value.riskTier || !RISK_TIERS.has(value.riskTier)) errors.push('economic.riskTier')
  if (typeof value.exposureDelta !== 'number' || !Number.isFinite(value.exposureDelta) || Math.abs(value.exposureDelta) > 1) errors.push('economic.exposureDelta')
  if (typeof value.costImpact !== 'number' || !Number.isFinite(value.costImpact) || value.costImpact < 0) errors.push('economic.costImpact')
  if (typeof value.entitlementTier !== 'string' || !value.entitlementTier.trim() || value.entitlementTier.length > MAX_STRING) errors.push('economic.entitlementTier')
  return { valid: errors.length === 0, errors }
}

export function validateGovernanceMetadata(metadata: unknown): GovernanceValidation {
  const errors: string[] = []
  if (!metadata || typeof metadata !== 'object') return { valid: false, errors: ['governance'] }
  const value = metadata as Partial<GovernanceMetadata>
  if (value.agent !== 'DESK' && value.agent !== 'JOB') errors.push('governance.agent')
  if (!['observe', 'plan', 'validate', 'mutate'].includes(value.capability || '')) errors.push('governance.capability')
  if (!['none', 'approve_plan', 'confirmed_control'].includes(value.mutationAuthority || '')) errors.push('governance.mutationAuthority')
  if (typeof value.decisionId !== 'string' || !value.decisionId.trim() || value.decisionId.length > MAX_STRING) errors.push('governance.decisionId')
  if (typeof value.stateHash !== 'string' || !/^[a-f0-9]{8,64}$/i.test(value.stateHash)) errors.push('governance.stateHash')
  if (!Array.isArray(value.lineage) || value.lineage.length === 0 || value.lineage.length > MAX_LINEAGE || value.lineage.some((item) => typeof item !== 'string' || !item.trim() || item.length > MAX_STRING)) errors.push('governance.lineage')
  errors.push(...validateEconomicTraceability(value.economic).errors)
  return { valid: errors.length === 0, errors }
}

function stableSerialize(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableSerialize).join(',')}]`
  if (value && typeof value === 'object') {
    return `{${Object.entries(value as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b)).map(([key, item]) => `${JSON.stringify(key)}:${stableSerialize(item)}`).join(',')}}`
  }
  return JSON.stringify(value)
}

function boundedDetails(details: Record<string, string | number | boolean>): Record<string, string | number | boolean> {
  return Object.fromEntries(Object.entries(details).slice(0, MAX_DETAILS).map(([key, value]) => [key.slice(0, MAX_STRING), typeof value === 'string' ? value.slice(0, MAX_STRING) : value]))
}

export class DeskGovernanceAuditLog {
  private readonly entries: GovernanceAuditEntry[] = []
  public constructor(private readonly maxEntries = MAX_ENTRIES) {}

  public append(entry: GovernanceAuditEntry): GovernanceAuditEntry | null {
    if (!validateGovernanceMetadata(entry.metadata).valid || !entry.stateHash || entry.stateHash !== entry.metadata.stateHash) return null
    if (this.entries.some((existing) => existing.id === entry.id)) return null
    const normalized: GovernanceAuditEntry = { ...entry, details: boundedDetails(entry.details), occurredAt: entry.occurredAt || new Date().toISOString() }
    this.entries.push(Object.freeze(normalized) as GovernanceAuditEntry)
    if (this.entries.length > this.maxEntries) this.entries.splice(0, this.entries.length - this.maxEntries)
    return normalized
  }

  public list(query: { kind?: GovernanceAuditKind; jobId?: string; limit?: number } = {}): readonly GovernanceAuditEntry[] {
    const limit = Math.min(Math.max(query.limit ?? 50, 1), 100)
    return this.entries.filter((entry) => (!query.kind || entry.kind === query.kind) && (!query.jobId || entry.details.jobId === query.jobId)).slice(-limit).reverse()
  }

  public page(query: { kind?: GovernanceAuditKind; jobId?: string; limit?: number; cursor?: string } = {}): GovernanceCursorPage {
    const limit = Math.min(Math.max(query.limit ?? 20, 1), 100)
    const filtered = this.entries.filter((entry) => (!query.kind || entry.kind === query.kind) && (!query.jobId || entry.details.jobId === query.jobId))
    const offset = query.cursor ? Number.parseInt(query.cursor, 10) : 0
    const start = Number.isFinite(offset) && offset >= 0 ? offset : 0
    const entries = filtered.slice().reverse().slice(start, start + limit)
    const next = start + entries.length
    return { entries, nextCursor: next < filtered.length ? String(next) : null, hasMore: next < filtered.length }
  }

  public summarize(limit = 20) {
    const entries = this.list({ limit })
    return {
      recentControlActions: entries.filter((entry) => entry.kind === 'control_action'),
      arbitrationEvents: entries.filter((entry) => entry.kind === 'arbitration'),
      verifierFailures: entries.filter((entry) => entry.kind === 'verifier_failure'),
      economicGuardrailHits: entries.filter((entry) => entry.kind === 'economic_guardrail'),
      stateLineage: entries.filter((entry) => entry.kind === 'job_state'),
      policyEvents: entries.filter((entry) => entry.kind === 'policy_event'),
      simulationEvents: entries.filter((entry) => entry.kind === 'simulation_event'),
      autonomyWindowEvents: entries.filter((entry) => entry.kind === 'autonomy_window_event'),
    }
  }

  public timeline(query: { limit?: number; cursor?: string; jobId?: string } = {}) {
    return this.page({ limit: query.limit, cursor: query.cursor, jobId: query.jobId }).entries.map((entry) => toTimelineEntry(entry))
  }

  public economicLineage(query: { limit?: number; cursor?: string; jobId?: string } = {}) {
    return this.page({ limit: query.limit, cursor: query.cursor, jobId: query.jobId }).entries.map((entry) => ({
      decisionId: entry.decisionId,
      parentStateHash: entry.parentStateHash ?? null,
      stateHash: entry.stateHash,
      economic: entry.metadata.economic,
      kind: entry.kind,
      replayToken: replayToken(entry),
    }))
  }

  /** Test-only reset; production code has no update/delete operation. */
  public resetForTests(): void { this.entries.splice(0, this.entries.length) }
}

export const deskGovernanceAuditLog = new DeskGovernanceAuditLog()

export function replayToken(entry: GovernanceAuditEntry): string {
  return `b5_${stableGovernanceId({ id: entry.id, kind: entry.kind, decisionId: entry.decisionId, stateHash: entry.stateHash, metadata: entry.metadata, details: entry.details })}`
}

export function replayAuditEntry(entry: GovernanceAuditEntry): GovernanceTimelineEntry {
  return toTimelineEntry(entry)
}

function toTimelineEntry(entry: GovernanceAuditEntry): GovernanceTimelineEntry {
  const phase = entry.metadata.lineage[entry.metadata.lineage.length - 1] ?? 'research'
  return {
    id: entry.id,
    occurredAt: entry.occurredAt,
    kind: entry.kind,
    agent: entry.agent,
    phase,
    decisionId: entry.decisionId,
    stateHash: entry.stateHash,
    economic: entry.metadata.economic,
    details: entry.details,
    replayToken: replayToken(entry),
  }
}

export function makeGovernanceMetadata(input: {
  agent: GovernanceAgent
  capability: GovernanceCapability
  mutationAuthority: MutationAuthority
  stateHash: string
  economic: EconomicTraceability
  lineage?: string[]
  decisionSeed: unknown
  parentDecisionId?: string
}): GovernanceMetadata {
  return {
    agent: input.agent,
    capability: input.capability,
    mutationAuthority: input.mutationAuthority,
    decisionId: stableGovernanceId(input.decisionSeed),
    ...(input.parentDecisionId ? { parentDecisionId: input.parentDecisionId } : {}),
    stateHash: input.stateHash,
    lineage: (input.lineage ?? ['research', 'validate', 'decide']).slice(0, MAX_LINEAGE),
    economic: input.economic,
  }
}
